import type { ReactNode } from 'react';
import { Caption1, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';

type MetricTone = 'neutral' | 'success' | 'warning' | 'danger';

const useStyles = makeStyles({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusLarge,
    minWidth: 0,
  },
  icon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  body: {
    display: 'flex',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
    flexWrap: 'wrap',
  },
  value: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightBold,
    lineHeight: 1,
  },
  label: {
    opacity: 0.8,
  },
  neutral: {
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground1,
  },
  success: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    color: tokens.colorPaletteGreenForeground1,
  },
  warning: {
    backgroundColor: tokens.colorPaletteYellowBackground1,
    color: tokens.colorPaletteYellowForeground1,
  },
  danger: {
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
  },
});

interface MetricPillProps {
  icon?: ReactNode;
  label: string;
  value: string | number;
  tone?: MetricTone;
}

export function MetricPill({ icon, label, tone = 'neutral', value }: MetricPillProps) {
  const styles = useStyles();
  const toneClass = styles[tone];

  return (
    <div className={mergeClasses(styles.root, toneClass)}>
      {icon ? <span className={styles.icon}>{icon}</span> : null}
      <div className={styles.body}>
        <span className={styles.value}>{value}</span>
        <Caption1 className={styles.label}>{label}</Caption1>
      </div>
    </div>
  );
}
